import React from 'react';
import { styled } from '@mui/material/styles';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Popper from '@mui/material/Popper';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Paper from '@mui/material/Paper';
import ListItemIcon from '@mui/material/ListItemIcon';
import Typography from '@mui/material/Typography';
import { Box } from '@mui/material';

const lightBlueColor = '#fdfdfd';

const StyledPaper = styled(Paper)({
  backgroundColor: lightBlueColor
});

const StyledListItemIcon = styled(ListItemIcon)({
  minWidth: 30
});

const StyledPopper = styled(Popper)({
  zIndex: 1,
  '&[x-placement*="bottom"] $arrow': {
    top: 0,
    left: 0,
    marginTop: '-0.9em',
    width: '3em',
    height: '1em',
    '&::before': {
      borderWidth: '0 1em 1em 1em',
      borderColor: `transparent transparent ${lightBlueColor} transparent`
    }
  },
  '&[x-placement*="top"] $arrow': {
    bottom: 0,
    left: 0,
    marginBottom: '-0.9em',
    width: '3em',
    height: '1em',
    '&::before': {
      borderWidth: '1em 1em 0 1em',
      borderColor: `${lightBlueColor} transparent transparent transparent`
    }
  },
  '&[x-placement*="right"] $arrow': {
    left: 0,
    marginLeft: '-0.9em',
    height: '3em',
    width: '1em',
    '&::before': {
      borderWidth: '1em 1em 1em 0',
      borderColor: `transparent ${lightBlueColor} transparent transparent`
    }
  },
  '&[x-placement*="left"] $arrow': {
    right: 0,
    marginRight: '-0.9em',
    height: '3em',
    width: '1em',
    '&::before': {
      borderWidth: '1em 0 1em 1em',
      borderColor: `transparent transparent transparent ${lightBlueColor}`
    }
  }
});

const Arrow = styled('span')({
  position: 'absolute',
  fontSize: 7,
  width: '3em',
  height: '3em',
  '&::before': {
    content: '""',
    margin: 'auto',
    display: 'block',
    width: 0,
    height: 0,
    borderStyle: 'solid'
  }
});

export default function DropButton({ icon, options, tooltip }) {
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef(null);
  const [arrowRef, setArrowRef] = React.useState(null);

  const handleToggle = (e) => {
    e.stopPropagation();
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current?.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  return (
    <>
      <Tooltip title={tooltip} placement="top" arrow>
        <IconButton
          ref={anchorRef}
          aria-controls={open ? 'drop-button-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-label="select from menu"
          aria-haspopup="true"
          onClick={handleToggle}
          size="large"
        >
          {icon}
        </IconButton>
      </Tooltip>
      <StyledPopper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement={'bottom-end'}
        style={{ zIndex: 11 }}
        // modifiers={{
        //   arrow: {
        //     enabled: true,
        //     element: arrowRef
        //   }
        // }}
        // transition
        // disablePortal
      >
        {/* <span className={classes.arrow} ref={setArrowRef} /> */}
        <StyledPaper>
          <ClickAwayListener onClickAway={handleClose}>
            <MenuList id="drop-button-menu">
              {options.map(
                (option) =>
                  option && (
                    <MenuItem key={option.label} onClick={option.onClick}>
                      {option.icon && (
                        <StyledListItemIcon>{option.icon}</StyledListItemIcon>
                      )}
                      <Typography variant="inherit" noWrap>
                        {option.label}
                      </Typography>
                    </MenuItem>
                  )
              )}
            </MenuList>
          </ClickAwayListener>
        </StyledPaper>
      </StyledPopper>
    </>
  );
}
